// @ts-nocheck
// TODO: tighten types after migration
import React from 'react';
import { renderLatex } from './latexRenderer';

/*
 * DiagramRenderer — generates SVG geometry visuals from [Diagram: ...] descriptions.
 *
 * Supports:
 *   - Linear pair (two angles on a line)
 *   - Angles on a straight line (2-5 angles summing to 180°)
 *   - Angles around a point (3-8 angles summing to 360°)
 *   - Angle bisector
 *   - Triangle (labeled vertices)
 *   - Circle (with center, radius)
 *   - Parallel lines with transversal
 *   - Perpendicular lines
 *   - Generic angle diagram
 *
 * Falls back to a styled text description if no pattern matches.
 */

// ── Utility helpers ─────────────────────────────────────────────────

function polarToXY(cx, cy, r, angleDeg) {
    var rad = (angleDeg * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) };
}

function arcPath(cx, cy, r, startDeg, endDeg) {
    var s = polarToXY(cx, cy, r, startDeg);
    var e = polarToXY(cx, cy, r, endDeg);
    var sweep = endDeg - startDeg;
    var largeArc = sweep > 180 ? 1 : 0;
    return 'M ' + s.x + ' ' + s.y + ' A ' + r + ' ' + r + ' 0 ' + largeArc + ' 0 ' + e.x + ' ' + e.y;
}

var COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];

// ── Pattern detection ───────────────────────────────────────────────

function detectDiagramType(desc) {
    var d = desc.toLowerCase();

    if (/linear\s*pair/i.test(d)) return 'linear_pair';
    if (/bisect/i.test(d)) return 'angle_bisector';
    if (/around\s*(a\s*)?(central\s*)?point/i.test(d) || /angles?\s*around/i.test(d) || /360/i.test(d)) return 'angles_around_point';
    if (/(on|along)\s*(a\s*)?(straight\s*)?line/i.test(d) || /supplementary/i.test(d) || /180/i.test(d)) return 'angles_on_line';
    if (/parallel/i.test(d) && /transversal/i.test(d)) return 'parallel_transversal';
    if (/parallel\s*lines/i.test(d)) return 'parallel_lines';
    if (/perpendicular/i.test(d)) return 'perpendicular';
    if (/triangle/i.test(d)) return 'triangle';
    if (/circle/i.test(d)) return 'circle';
    if (/quadrilateral|square|rectangle|rhombus|parallelogram/i.test(d)) return 'quadrilateral';
    if (/angle/i.test(d)) return 'generic_angle';

    return null;
}

function extractLabels(desc) {
    // Extract angle labels like ∠1, ∠2, ∠XOY, ∠ABC
    var angleLabels = [];
    var angleRegex = /∠([A-Z]{2,3}|\d+)/g;
    var m;
    while ((m = angleRegex.exec(desc)) !== null) {
        angleLabels.push('∠' + m[1]);
    }

    // Extract point labels (single uppercase letters)
    var pointLabels = [];
    var pointRegex = /\b([A-Z])\b/g;
    while ((m = pointRegex.exec(desc)) !== null) {
        if (pointLabels.indexOf(m[1]) === -1) pointLabels.push(m[1]);
    }

    // Extract count of angles mentioned
    var countMatch = desc.match(/(\w+)\s+angles?/i);
    var angleCount = 0;
    if (countMatch) {
        var words = { two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8 };
        angleCount = words[countMatch[1].toLowerCase()] || parseInt(countMatch[1]) || 0;
    }
    if (!angleCount && angleLabels.length > 0) angleCount = angleLabels.length;

    return { angleLabels: angleLabels, pointLabels: pointLabels, angleCount: angleCount };
}

// ── SVG Diagram Generators ──────────────────────────────────────────

function drawLinearPair(desc, parsed) {
    var cx = 160, cy = 130, rayLen = 120, arcR = 40;
    var labels = parsed.angleLabels.length >= 2 ? parsed.angleLabels : ['∠XOY', '∠YOZ'];
    var points = parsed.pointLabels.length >= 3 ? parsed.pointLabels : ['X', 'O', 'Y', 'Z'];
    var origin = points.length >= 4 ? points[1] : 'O';
    var leftPt = points[0] || 'X';
    var rayPt = points.length >= 3 ? points[2] : 'Y';
    var rightPt = points.length >= 4 ? points[3] : 'Z';

    // Straight line left to right, ray going up
    var leftEnd = { x: cx - rayLen, y: cy };
    var rightEnd = { x: cx + rayLen, y: cy };
    var rayEnd = polarToXY(cx, cy, rayLen, 120); // Ray at 120° from horizontal

    return (
        <svg viewBox="0 0 320 180" className="dv-diagram-svg">
            {/* Straight line */}
            <line x1={leftEnd.x} y1={leftEnd.y} x2={rightEnd.x} y2={rightEnd.y} stroke="#64748b" strokeWidth="2" />
            {/* Ray */}
            <line x1={cx} y1={cy} x2={rayEnd.x} y2={rayEnd.y} stroke="#64748b" strokeWidth="2" />
            {/* Origin dot */}
            <circle cx={cx} cy={cy} r="3" fill="#1e40af" />

            {/* Angle arcs */}
            <path d={arcPath(cx, cy, arcR, 0, 120)} fill="none" stroke={COLORS[0]} strokeWidth="2" />
            <path d={arcPath(cx, cy, arcR * 0.7, 120, 180)} fill="none" stroke={COLORS[1]} strokeWidth="2" />

            {/* Angle fills */}
            <path d={'M ' + cx + ' ' + cy + ' L ' + (cx + arcR) + ' ' + cy + ' ' + arcPath(cx, cy, arcR, 0, 120).slice(arcPath(cx, cy, arcR, 0, 120).indexOf('A')) + ' Z'}
                fill={COLORS[0]} opacity="0.1" />
            <path d={'M ' + cx + ' ' + cy + ' L ' + polarToXY(cx, cy, arcR, 120).x + ' ' + polarToXY(cx, cy, arcR, 120).y + ' ' + arcPath(cx, cy, arcR * 0.7, 120, 180).slice(arcPath(cx, cy, arcR * 0.7, 120, 180).indexOf('A')) + ' Z'}
                fill={COLORS[1]} opacity="0.1" />

            {/* Labels */}
            <text x={leftEnd.x - 5} y={leftEnd.y + 18} className="dv-diagram-label">{leftPt}</text>
            <text x={cx} y={cy + 18} className="dv-diagram-label" textAnchor="middle">{origin}</text>
            <text x={rayEnd.x - 12} y={rayEnd.y - 5} className="dv-diagram-label">{rayPt}</text>
            <text x={rightEnd.x + 5} y={rightEnd.y + 18} className="dv-diagram-label">{rightPt}</text>

            {/* Angle names */}
            {labels[0] && <text x={cx + 50} y={cy - 30} className="dv-diagram-angle-label" fill={COLORS[0]}>{labels[0]}</text>}
            {labels[1] && <text x={cx - 55} y={cy - 15} className="dv-diagram-angle-label" fill={COLORS[1]}>{labels[1]}</text>}
        </svg>
    );
}

function drawAnglesOnLine(desc, parsed) {
    var cx = 160, cy = 140, rayLen = 120, arcR = 35;
    var count = parsed.angleCount || 3;
    if (count < 2) count = 2;
    if (count > 5) count = 5;

    var labels = parsed.angleLabels;
    if (labels.length < count) {
        labels = [];
        for (var n = 1; n <= count; n++) labels.push('∠' + n);
    }

    // Divide 180° into count parts
    var step = 180 / count;
    var rays = [];
    for (var a = 0; a <= count; a++) {
        rays.push(polarToXY(cx, cy, rayLen, a * step));
    }

    var elements = [];

    // Straight line
    elements.push(<line key="base" x1={cx - rayLen} y1={cy} x2={cx + rayLen} y2={cy} stroke="#64748b" strokeWidth="2" />);

    // Rays (skip first and last which are on the line)
    for (var r = 1; r < count; r++) {
        elements.push(<line key={'ray' + r} x1={cx} y1={cy} x2={rays[r].x} y2={rays[r].y} stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="4,3" />);
    }

    // Arcs and labels
    for (var j = 0; j < count; j++) {
        var startAngle = j * step;
        var endAngle = (j + 1) * step;
        var midAngle = (startAngle + endAngle) / 2;
        var color = COLORS[j % COLORS.length];
        var labelPos = polarToXY(cx, cy, arcR + 18, midAngle);

        elements.push(<path key={'arc' + j} d={arcPath(cx, cy, arcR + j * 5, startAngle, endAngle)} fill="none" stroke={color} strokeWidth="2" />);
        elements.push(<text key={'lbl' + j} x={labelPos.x} y={labelPos.y} className="dv-diagram-angle-label" fill={color} textAnchor="middle" dominantBaseline="middle">{labels[j]}</text>);
    }

    // Origin dot
    elements.push(<circle key="origin" cx={cx} cy={cy} r="3" fill="#1e40af" />);

    // Sum label
    elements.push(<text key="sum" x={cx} y={20} className="dv-diagram-sum-label" textAnchor="middle">{labels.join(' + ') + ' = 180°'}</text>);

    return <svg viewBox="0 0 320 170" className="dv-diagram-svg">{elements}</svg>;
}

function drawAnglesAroundPoint(desc, parsed) {
    var cx = 160, cy = 120, rayLen = 90, arcR = 30;
    var count = parsed.angleCount || 5;
    if (count < 3) count = 3;
    if (count > 8) count = 8;

    var labels = parsed.angleLabels;
    if (labels.length < count) {
        labels = [];
        for (var n = 1; n <= count; n++) labels.push('∠' + n);
    }

    // Divide 360° into count unequal-ish parts for visual interest
    var angles = [];
    var base = 360 / count;
    for (var a = 0; a < count; a++) {
        // Slight variation
        angles.push(base + (a % 2 === 0 ? 5 : -5));
    }
    // Normalize to sum to 360
    var sum = angles.reduce(function(s, v) { return s + v; }, 0);
    angles = angles.map(function(v) { return (v / sum) * 360; });

    var elements = [];
    var cumAngle = 0;

    for (var j = 0; j < count; j++) {
        var startAngle = cumAngle;
        var endAngle = cumAngle + angles[j];
        var midAngle = (startAngle + endAngle) / 2;
        var color = COLORS[j % COLORS.length];
        var rayEnd = polarToXY(cx, cy, rayLen, startAngle);
        var labelPos = polarToXY(cx, cy, arcR + 20, midAngle);

        // Ray
        elements.push(<line key={'ray' + j} x1={cx} y1={cy} x2={rayEnd.x} y2={rayEnd.y} stroke="#94a3b8" strokeWidth="1.5" />);

        // Arc
        elements.push(<path key={'arc' + j} d={arcPath(cx, cy, arcR + (j % 2) * 8, startAngle, endAngle)} fill="none" stroke={color} strokeWidth="2" />);

        // Label
        elements.push(<text key={'lbl' + j} x={labelPos.x} y={labelPos.y} className="dv-diagram-angle-label" fill={color} textAnchor="middle" dominantBaseline="middle">{labels[j]}</text>);

        cumAngle = endAngle;
    }
    // Last ray to close
    var lastRay = polarToXY(cx, cy, rayLen, cumAngle);
    elements.push(<line key="raylast" x1={cx} y1={cy} x2={lastRay.x} y2={lastRay.y} stroke="#94a3b8" strokeWidth="1.5" />);

    // Center dot
    elements.push(<circle key="center" cx={cx} cy={cy} r="3" fill="#1e40af" />);

    // Sum label
    elements.push(<text key="sum" x={cx} y={15} className="dv-diagram-sum-label" textAnchor="middle">{labels.join(' + ') + ' = 360°'}</text>);

    return <svg viewBox="0 0 320 240" className="dv-diagram-svg">{elements}</svg>;
}

function drawAngleBisector(desc, parsed) {
    var cx = 160, cy = 150, rayLen = 120, arcR = 45;
    var points = parsed.pointLabels.length >= 3 ? parsed.pointLabels : ['X', 'O', 'Y', 'Z'];

    // Two rays forming an angle, bisector in the middle
    var angle1 = 30;
    var angle2 = 150;
    var bisector = (angle1 + angle2) / 2;

    var ray1End = polarToXY(cx, cy, rayLen, angle1);
    var ray2End = polarToXY(cx, cy, rayLen, angle2);
    var bisEnd = polarToXY(cx, cy, rayLen, bisector);

    return (
        <svg viewBox="0 0 320 180" className="dv-diagram-svg">
            {/* Rays */}
            <line x1={cx} y1={cy} x2={ray1End.x} y2={ray1End.y} stroke="#64748b" strokeWidth="2" />
            <line x1={cx} y1={cy} x2={ray2End.x} y2={ray2End.y} stroke="#64748b" strokeWidth="2" />
            {/* Bisector */}
            <line x1={cx} y1={cy} x2={bisEnd.x} y2={bisEnd.y} stroke={COLORS[0]} strokeWidth="2" strokeDasharray="6,3" />

            {/* Arcs — equal halves */}
            <path d={arcPath(cx, cy, arcR, angle1, bisector)} fill="none" stroke={COLORS[1]} strokeWidth="2" />
            <path d={arcPath(cx, cy, arcR + 8, bisector, angle2)} fill="none" stroke={COLORS[2]} strokeWidth="2" />

            {/* Equal marks */}
            {[arcR, arcR + 8].map(function(r, idx) {
                var tickAngle = idx === 0 ? (angle1 + bisector) / 2 : (bisector + angle2) / 2;
                var p = polarToXY(cx, cy, r, tickAngle);
                return <text key={'tick' + idx} x={p.x} y={p.y} textAnchor="middle" dominantBaseline="middle" fontSize="10" fill={COLORS[idx + 1]}>|</text>;
            })}

            {/* Origin dot */}
            <circle cx={cx} cy={cy} r="3" fill="#1e40af" />

            {/* Labels */}
            <text x={ray1End.x + 8} y={ray1End.y + 5} className="dv-diagram-label">{points[2] || 'Z'}</text>
            <text x={cx - 3} y={cy + 18} className="dv-diagram-label" textAnchor="middle">{points[1] || 'O'}</text>
            <text x={bisEnd.x + 5} y={bisEnd.y - 8} className="dv-diagram-label" fill={COLORS[0]}>{points[2] || 'Y'}</text>
            <text x={ray2End.x - 12} y={ray2End.y - 5} className="dv-diagram-label">{points[0] || 'X'}</text>

            {/* Bisector label */}
            <text x={cx + 75} y={cy - 55} className="dv-diagram-info-label" fill={COLORS[0]}>Bisector</text>
        </svg>
    );
}

function drawTriangle(desc, parsed) {
    var labels = parsed.pointLabels.length >= 3 ? parsed.pointLabels.slice(0, 3) : ['A', 'B', 'C'];

    // Check for special triangles
    var isEquilateral = /equilateral/i.test(desc);
    var isIsosceles = /isosceles/i.test(desc);
    var isRight = /right/i.test(desc);

    var pts;
    if (isEquilateral) {
        pts = [{ x: 160, y: 30 }, { x: 60, y: 200 }, { x: 260, y: 200 }];
    } else if (isRight) {
        pts = [{ x: 60, y: 30 }, { x: 60, y: 200 }, { x: 260, y: 200 }];
    } else if (isIsosceles) {
        pts = [{ x: 160, y: 30 }, { x: 70, y: 200 }, { x: 250, y: 200 }];
    } else {
        pts = [{ x: 140, y: 30 }, { x: 50, y: 200 }, { x: 270, y: 200 }];
    }

    var path = 'M ' + pts[0].x + ' ' + pts[0].y + ' L ' + pts[1].x + ' ' + pts[1].y + ' L ' + pts[2].x + ' ' + pts[2].y + ' Z';

    // Extract angle values if present
    var angleValues = desc.match(/(\d+)°/g) || [];

    return (
        <svg viewBox="0 0 320 230" className="dv-diagram-svg">
            {/* Triangle fill */}
            <path d={path} fill="#3b82f6" opacity="0.06" stroke="#3b82f6" strokeWidth="2" strokeLinejoin="round" />

            {/* Right angle marker */}
            {isRight && (
                <path d={'M ' + (pts[1].x + 18) + ' ' + pts[1].y + ' L ' + (pts[1].x + 18) + ' ' + (pts[1].y - 18) + ' L ' + pts[1].x + ' ' + (pts[1].y - 18)}
                    fill="none" stroke="#64748b" strokeWidth="1.5" />
            )}

            {/* Vertex labels */}
            <text x={pts[0].x} y={pts[0].y - 10} className="dv-diagram-label" textAnchor="middle">{labels[0]}</text>
            <text x={pts[1].x - 15} y={pts[1].y + 5} className="dv-diagram-label" textAnchor="middle">{labels[1]}</text>
            <text x={pts[2].x + 15} y={pts[2].y + 5} className="dv-diagram-label" textAnchor="middle">{labels[2]}</text>

            {/* Angle values if present */}
            {angleValues.length >= 1 && <text x={pts[0].x} y={pts[0].y + 25} className="dv-diagram-measure" textAnchor="middle">{angleValues[0]}</text>}
            {angleValues.length >= 2 && <text x={pts[1].x + 20} y={pts[1].y - 10} className="dv-diagram-measure" textAnchor="middle">{angleValues[1]}</text>}
            {angleValues.length >= 3 && <text x={pts[2].x - 20} y={pts[2].y - 10} className="dv-diagram-measure" textAnchor="middle">{angleValues[2]}</text>}

            {/* Vertex dots */}
            {pts.map(function(p, i) { return <circle key={i} cx={p.x} cy={p.y} r="3" fill="#1e40af" />; })}
        </svg>
    );
}

function drawCircle(desc, parsed) {
    var cx = 160, cy = 120, r = 80;
    var center = parsed.pointLabels[0] || 'O';
    var radiusPt = parsed.pointLabels[1] || 'A';
    var hasDiameter = /diameter/i.test(desc);
    var hasChord = /chord/i.test(desc);
    var hasTangent = /tangent/i.test(desc);

    return (
        <svg viewBox="0 0 320 240" className="dv-diagram-svg">
            {/* Circle */}
            <circle cx={cx} cy={cy} r={r} fill="#3b82f6" opacity="0.05" stroke="#3b82f6" strokeWidth="2" />

            {/* Center dot */}
            <circle cx={cx} cy={cy} r="3" fill="#1e40af" />
            <text x={cx - 12} y={cy + 5} className="dv-diagram-label">{center}</text>

            {/* Radius */}
            <line x1={cx} y1={cy} x2={cx + r} y2={cy} stroke={COLORS[0]} strokeWidth="1.5" />
            <text x={cx + r + 10} y={cy + 5} className="dv-diagram-label">{radiusPt}</text>
            <text x={cx + r / 2} y={cy - 8} className="dv-diagram-measure">r</text>

            {/* Diameter if applicable */}
            {hasDiameter && (
                <>
                    <line x1={cx - r} y1={cy} x2={cx + r} y2={cy} stroke={COLORS[1]} strokeWidth="1.5" />
                    <text x={cx - r - 15} y={cy + 5} className="dv-diagram-label">{parsed.pointLabels[2] || 'B'}</text>
                </>
            )}

            {/* Chord */}
            {hasChord && (
                <line x1={cx - r * 0.7} y1={cy - r * 0.7} x2={cx + r * 0.5} y2={cy + r * 0.85}
                    stroke={COLORS[2]} strokeWidth="1.5" strokeDasharray="5,3" />
            )}

            {/* Tangent */}
            {hasTangent && (
                <line x1={cx + r - 60} y1={cy + r + 30} x2={cx + r + 60} y2={cy + r + 30}
                    stroke={COLORS[3]} strokeWidth="1.5" />
            )}
        </svg>
    );
}

function drawParallelTransversal(desc, parsed) {
    return (
        <svg viewBox="0 0 320 220" className="dv-diagram-svg">
            {/* Parallel line 1 */}
            <line x1="40" y1="60" x2="280" y2="60" stroke="#64748b" strokeWidth="2" />
            {/* Parallel line 2 */}
            <line x1="40" y1="160" x2="280" y2="160" stroke="#64748b" strokeWidth="2" />

            {/* Parallel arrows */}
            <text x="290" y="63" fontSize="12" fill="#64748b">›</text>
            <text x="290" y="163" fontSize="12" fill="#64748b">›</text>

            {/* Transversal */}
            <line x1="100" y1="20" x2="220" y2="200" stroke={COLORS[0]} strokeWidth="2" />

            {/* Intersection dots */}
            <circle cx="127" cy="60" r="3" fill="#1e40af" />
            <circle cx="193" cy="160" r="3" fill="#1e40af" />

            {/* Angle arcs at intersection 1 */}
            <path d={arcPath(127, 60, 20, 0, 65)} fill="none" stroke={COLORS[1]} strokeWidth="2" />
            <path d={arcPath(127, 60, 25, 180, 245)} fill="none" stroke={COLORS[2]} strokeWidth="2" />

            {/* Angle arcs at intersection 2 */}
            <path d={arcPath(193, 160, 20, 0, 65)} fill="none" stroke={COLORS[1]} strokeWidth="2" />
            <path d={arcPath(193, 160, 25, 180, 245)} fill="none" stroke={COLORS[2]} strokeWidth="2" />

            {/* Labels */}
            <text x="30" y="55" className="dv-diagram-label">l₁</text>
            <text x="30" y="155" className="dv-diagram-label">l₂</text>
            <text x="90" y="18" className="dv-diagram-label" fill={COLORS[0]}>t</text>
        </svg>
    );
}

function drawGenericAngle(desc, parsed) {
    var cx = 80, cy = 150, rayLen = 140, arcR = 40;
    var labels = parsed.angleLabels.length > 0 ? parsed.angleLabels : ['∠θ'];
    var points = parsed.pointLabels.length >= 3 ? parsed.pointLabels : ['A', 'O', 'B'];

    var angle1 = 0;
    var angle2 = 60;

    var angleMatch = desc.match(/(\d+)°/);
    if (angleMatch) {
        angle2 = parseInt(angleMatch[1]);
        if (angle2 > 170) angle2 = 60;
    }

    var ray1End = polarToXY(cx, cy, rayLen, angle1);
    var ray2End = polarToXY(cx, cy, rayLen, angle2);
    var labelPos = polarToXY(cx, cy, arcR + 22, angle2 / 2);

    return (
        <svg viewBox="0 0 320 180" className="dv-diagram-svg">
            <line x1={cx} y1={cy} x2={ray1End.x} y2={ray1End.y} stroke="#64748b" strokeWidth="2" />
            <line x1={cx} y1={cy} x2={ray2End.x} y2={ray2End.y} stroke="#64748b" strokeWidth="2" />
            <path d={arcPath(cx, cy, arcR, angle1, angle2)} fill="none" stroke={COLORS[0]} strokeWidth="2" />

            <circle cx={cx} cy={cy} r="3" fill="#1e40af" />

            <text x={cx - 5} y={cy + 18} className="dv-diagram-label" textAnchor="middle">{points[1] || 'O'}</text>
            <text x={ray1End.x + 8} y={ray1End.y + 5} className="dv-diagram-label">{points[2] || 'B'}</text>
            <text x={ray2End.x + 8} y={ray2End.y - 5} className="dv-diagram-label">{points[0] || 'A'}</text>
            <text x={labelPos.x} y={labelPos.y} className="dv-diagram-angle-label" fill={COLORS[0]} textAnchor="middle">{labels[0]}</text>
        </svg>
    );
}

function drawQuadrilateral(desc, parsed) {
    var labels = parsed.pointLabels.length >= 4 ? parsed.pointLabels.slice(0, 4) : ['A', 'B', 'C', 'D'];
    var isSquare = /square/i.test(desc);
    var isRectangle = /rectangle/i.test(desc);

    var pts;
    if (isSquare) {
        pts = [{ x: 90, y: 40 }, { x: 230, y: 40 }, { x: 230, y: 180 }, { x: 90, y: 180 }];
    } else if (isRectangle) {
        pts = [{ x: 60, y: 50 }, { x: 260, y: 50 }, { x: 260, y: 180 }, { x: 60, y: 180 }];
    } else {
        pts = [{ x: 100, y: 30 }, { x: 240, y: 50 }, { x: 260, y: 190 }, { x: 70, y: 170 }];
    }

    var path = pts.map(function(p, i) { return (i === 0 ? 'M ' : 'L ') + p.x + ' ' + p.y; }).join(' ') + ' Z';
    var offsets = [{ dx: 0, dy: -12 }, { dx: 12, dy: -5 }, { dx: 12, dy: 15 }, { dx: -12, dy: 15 }];

    return (
        <svg viewBox="0 0 320 220" className="dv-diagram-svg">
            <path d={path} fill="#3b82f6" opacity="0.06" stroke="#3b82f6" strokeWidth="2" strokeLinejoin="round" />
            {pts.map(function(p, i) {
                return (
                    <React.Fragment key={i}>
                        <circle cx={p.x} cy={p.y} r="3" fill="#1e40af" />
                        <text x={p.x + offsets[i].dx} y={p.y + offsets[i].dy} className="dv-diagram-label" textAnchor="middle">{labels[i]}</text>
                    </React.Fragment>
                );
            })}
            {(isSquare || isRectangle) && (
                <path d={'M ' + (pts[0].x + 15) + ' ' + pts[0].y + ' L ' + (pts[0].x + 15) + ' ' + (pts[0].y + 15) + ' L ' + pts[0].x + ' ' + (pts[0].y + 15)}
                    fill="none" stroke="#64748b" strokeWidth="1" />
            )}
        </svg>
    );
}

// ── Main Renderer ───────────────────────────────────────────────────

var RENDERERS = {
    linear_pair: drawLinearPair,
    angles_on_line: drawAnglesOnLine,
    angles_around_point: drawAnglesAroundPoint,
    angle_bisector: drawAngleBisector,
    triangle: drawTriangle,
    circle: drawCircle,
    parallel_transversal: drawParallelTransversal,
    parallel_lines: drawParallelTransversal,
    perpendicular: drawGenericAngle,
    generic_angle: drawGenericAngle,
    quadrilateral: drawQuadrilateral,
};

export function renderDiagram(description, key, onSelect) {
    if (!description) return null;

    var type = detectDiagramType(description);
    var parsed = extractLabels(description);
    var renderer = type ? RENDERERS[type] : null;
    var svgElement = null;

    try {
        if (renderer) {
            svgElement = renderer(description, parsed);
        }
    } catch (e) {
        svgElement = null;
    }

    var handleClick = onSelect ? function() { onSelect(description); } : undefined;

    return (
        <div key={key} className={'dv-diagram-visual' + (onSelect ? ' dv-diagram-selectable' : '')} onClick={handleClick} role={onSelect ? 'button' : undefined} tabIndex={onSelect ? 0 : undefined}>
            {svgElement ? (
                <>
                    <div className="dv-diagram-svg-wrap">{svgElement}</div>
                    <div className="dv-diagram-caption">{renderLatex(description)}</div>
                </>
            ) : (
                <div className="dv-diagram-fallback">
                    <div className="dv-diagram-fallback-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <polygon points="12 2 2 22 22 22" />
                            <circle cx="12" cy="16" r="0.5" />
                        </svg>
                    </div>
                    <div className="dv-diagram-fallback-text">{renderLatex(description)}</div>
                </div>
            )}
        </div>
    );
}

export default renderDiagram;
